/**
 * Customize orderCopier to add the merge: true to be able to reorder the same order without issues
 * Customize getCart to not fetch cart data server side.
 */

import {
	cartCalculator,
	cartSummaryFetcher,
	cartUpdateRewardOption,
} from '@/core/data/Content/_Cart';
import { SKIP_ERROR_LOGGING } from '@/data/constants/common';
import { DATA_KEY_CART } from '@/data/constants/dataKey';
import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { getLocalization } from '@/data/Localization-Server';
import { dDiv, dFix } from '@/data/Settings-Server';
import { ID } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { Order } from '@/data/types/Order';
import { error as logError } from '@/data/utils/loggerUtil';
import { RequestParams } from 'integration/generated/transactions/http-client';
import transactionsCart from 'integration/generated/transactions/transactionsCart';
import { GetServerSidePropsContext } from 'next';

export const DATA_KEY = DATA_KEY_CART;

type FullFetcherProps = {
	storeId: string;
	query: Record<string, string | boolean | ID[]>;
	params: RequestParams;
};

const fetcherFull = (pub: boolean) => async (props: FullFetcherProps) => {
	const { storeId, query, params } = props;
	let totalPages = 1;

	// an empty cart responds 204 No Content, which the generated client resolves to `null` --
	//   normalize it to an empty order so consumers can tell "empty cart" from "still loading"
	const cart =
		(await (transactionsCart(pub).cartGetCart(
			storeId,
			query,
			params
		) as Promise<unknown> as Promise<Order>)) ?? ({} as Order);

	const { recordSetCount = 0, recordSetTotal = 0, orderItem = [] } = cart;

	const pageSize = dFix(recordSetCount, 0);
	if (pageSize < dFix(recordSetTotal, 0)) {
		totalPages = dFix(Math.ceil(dDiv(recordSetTotal, pageSize)), 0);
	}

	if (totalPages > 1) {
		// generate fetches for remaining pages
		const fetches = Array.from(
			{ length: totalPages - 1 },
			(_empty, index) =>
				transactionsCart(pub).cartGetCart(
					storeId,
					{ ...query, pageNumber: index + 2, pageSize },
					params
				) as Promise<unknown> as Promise<Order>
		);

		// fetch remaining pages concurrently
		const pages = await Promise.all(fetches);

		// collect all order-items
		const allItems = [...orderItem, ...pages.map(({ orderItem }) => orderItem).flat(1)];

		// update the container
		cart.orderItem = allItems;
	}

	return cart;
};

export const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	/**
	 * The data fetcher for cart
	 * @param query The request query.
	 * @param params The RequestParams, it contains all the info that a request needed except for 'body' | 'method' | 'query' | 'path'.
	 *                                  we are using it to send cookie header.
	 * @returns Fetched cart data.
	 */
	async (
		storeId: string,
		query: {
			langId: string;
			[key: string]: string | boolean | ID[];
		},
		params: RequestParams
	): Promise<Order | undefined> => {
		try {
			return await fetcherFull(pub)({
				storeId,
				query,
				params: { skipErrorLogging: SKIP_ERROR_LOGGING, ...params },
			});
		} catch (error: any) {
			if (error.status === 404) {
				return {} as Order;
			}
			logError(context?.req, '_Cart: fetcher: error: %o', error);
			if (pub) {
				throw error;
			}
			return undefined;
		}
	};

export const orderCopier =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		fromOrderId: fromOrderId_1,
		storeId,
		langId,
		query = {},
		params,
	}: {
		fromOrderId: string;
		storeId: string;
		langId: string;
		query: { responseFormat?: 'xml' | 'json' } | undefined;
		params: RequestParams;
	}) => {
		const data = {
			merge: 'true',
			fromOrderId_1,
			toOrderId: '.**.',
			copyOrderItemId_1: '*',
		};
		try {
			await transactionsCart(pub).cartCopyOrder(storeId, query, data, params);
			return await fetcher(pub)(storeId, { langId }, params);
		} catch (e) {
			logError(context?.req, 'Error in copying order %o', e);
			if (pub) {
				throw e;
			}
			// currently, we do not want to break the server with error
			return undefined;
		}
	};

export const cartCalculatorByCalculationUsage =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async ({
		storeId,
		query = {},
		calculationUsageId,
		params,
	}: {
		storeId: string;
		query?: { responseFormat?: 'xml' | 'json' };
		calculationUsageId: string;
		params: RequestParams;
	}) => {
		try {
			return await transactionsCart(pub).cartCalculateOrder1(
				storeId,
				query,
				{ calculationUsageId },
				params
			);
		} catch (e) {
			logError(context?.req, 'Error in calculating order %o', e);
			if (pub) {
				throw e;
			}
			// currently, we do not want to break the server with error
			return undefined;
		}
	};

export const getCart = async ({
	cache,
	id: _id,
	context,
}: ContentProps): Promise<Order | undefined> => {
	const { localeName: locale } = await getStoreLocale({ cache, context });
	await Promise.all([
		getLocalization(cache, locale, 'Routes'),
		getLocalization(cache, locale, 'Cart'),
		getLocalization(cache, locale, 'FreeGift'),
		getLocalization(cache, locale, 'OrderItemTable'),
	]);

	return undefined;
};

export { cartCalculator, cartSummaryFetcher, cartUpdateRewardOption };
